import { createHash } from 'crypto';
import stringify from 'fast-json-stable-stringify';
import { isEqual, sortBy } from 'lodash';
import { DateTime } from 'luxon';
import memoizeOne from 'memoize-one';

import { mkFilterObject } from 'app-components/flux/filters/filtersTypes';
import type { FilterObjectProps } from 'app-components/flux/filters/filtersTypes';
import type { DensityType, ReportNameType } from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { constructPredefinedInterval, getStartOfToday } from 'app-components/utils/date';

import type { ReportSubTypeWebReports, ReportsState } from 'containers/NewReports/types';
import { assert } from 'utils/assert';

//-------------------------------------------------------------------------------------------------
// Tab Related Helpers
//-------------------------------------------------------------------------------------------------
export function convertTabNameToTitle(tabName: ReportNameType) {
  switch (tabName) {
    case 'total-spending':
      return 'Reports: Spending';
    case 'income':
      return 'Reports: Income';
    case 'net-income':
      return 'Reports: Net Income';
    case 'profit-loss':
      return 'Reports: Profit & Loss';
    case 'savings':
      return 'Reports: Savings';
    case 'net-worth':
      return 'Reports: Net Worth';
    case 'monthly-summary':
      return 'Reports: Summary';
    case 'taxes':
      return 'Reports: Taxes';
    case 'balance-sheet':
      return 'Reports: Balance Sheet';
    default:
      assert(false, `unexpected report tab name: ${tabName}`);
      return 'Reports';
  }
}

export const defaultDisplayOptions = {
  alternateRows: false,
  density: 'COMPACT' as DensityType,
  hideCents: false,
  showExpensesAsNegative: false,
  showOtherOnTop: false,
  showSubTotalsOnTop: false,
  showPLGraph: false,
};

type BuildDefaultsByTabNameReturnType = Record<string, ReportsState>;

const buildDefaultsByTabName = (
  today: DateTime,
  businessEnabled: boolean,
  defaultBusinessId?: string,
  openBusinessesIds: string[] = [],
): BuildDefaultsByTabNameReturnType => ({
  'total-spending': {
    name: 'total-spending',
    reportType: 'SUMMARY',
    reportSubType: 'SPENDING',
    dateRange: { id: 'MONTH_TO_DATE', interval: constructPredefinedInterval({ value: 'MONTH_TO_DATE' }, today) },
    groupBy: 'CATEGORY',
    segmentBy: 'TIME',
    timestep: 'NONE',
    chartType: 'PIE',
    tabName: 'total-spending',
    today,
    accountingType: 'CASH',
    breadcrumbs: [],
    filter: mkFilterObject(),
    options: { viewToShow: 'GRAPH_AND_GRID', showInterval: false },
    displayOptions: defaultDisplayOptions,
    loading: false,
    usage: businessEnabled ? openBusinessesIds.concat(['PERSONAL']) : undefined,
  },
  income: {
    name: 'income',
    reportType: 'SUMMARY',
    reportSubType: 'INCOME',
    dateRange: { id: 'LAST_6_MONTHS', interval: constructPredefinedInterval({ value: 'LAST_6_MONTHS' }, today) },
    groupBy: 'NONE',
    segmentBy: 'TIME',
    timestep: 'MONTH',
    chartType: 'BAR_STACKED',
    tabName: 'income',
    today,
    accountingType: 'CASH',
    breadcrumbs: [],
    filter: mkFilterObject(),
    options: {},
    displayOptions: defaultDisplayOptions,
    loading: false,
    usage: businessEnabled ? openBusinessesIds.concat(['PERSONAL']) : undefined,
  },
  'net-income': {
    name: 'net-income',
    reportType: 'SUMMARY',
    reportSubType: 'NET_INCOME',
    dateRange: { id: 'LAST_6_MONTHS', interval: constructPredefinedInterval({ value: 'LAST_6_MONTHS' }, today) },
    groupBy: 'CATEGORY',
    segmentBy: 'TIME',
    timestep: 'MONTH',
    chartType: 'NET_INCOME',
    tabName: 'net-income',
    today,
    accountingType: 'CASH',
    breadcrumbs: [],
    filter: mkFilterObject(),
    options: {},
    displayOptions: defaultDisplayOptions,
    loading: false,
    usage: businessEnabled ? openBusinessesIds.concat(['PERSONAL']) : undefined,
  },
  'profit-loss': {
    name: 'profit-loss',
    reportType: 'SUMMARY',
    reportSubType: 'NET_INCOME',
    dateRange: { id: 'LAST_6_MONTHS', interval: constructPredefinedInterval({ value: 'LAST_6_MONTHS' }, today) },
    groupBy: 'CATEGORY',
    segmentBy: 'TIME',
    timestep: 'MONTH',
    chartType: 'NET_INCOME',
    tabName: 'profit-loss',
    today,
    accountingType: 'CASH',
    breadcrumbs: [],
    filter: mkFilterObject(),
    options: {
      viewToShow: 'GRAPH_AND_GRID',
      showInterval: false,
    },
    displayOptions: { ...defaultDisplayOptions, showPLGraph: true },
    loading: false,
    usage: businessEnabled ? openBusinessesIds : undefined,
  },
  savings: {
    name: 'savings',
    reportType: 'SUMMARY',
    reportSubType: 'NONE',
    dateRange: { id: 'LAST_6_MONTHS', interval: constructPredefinedInterval({ value: 'LAST_6_MONTHS' }, today) },
    groupBy: 'NONE',
    segmentBy: 'TIME',
    timestep: 'MONTH',
    chartType: 'MULTILINE',
    tabName: 'savings',
    today,
    accountingType: 'CASH',
    breadcrumbs: [],
    filter: mkFilterObject(),
    options: {},
    displayOptions: defaultDisplayOptions,
    loading: false,
    usage: businessEnabled ? openBusinessesIds.concat(['PERSONAL']) : undefined,
  },
  'net-worth': {
    name: 'net-worth',
    reportType: 'SUMMARY',
    reportSubType: 'NET_WORTH',
    dateRange: { id: 'LAST_6_MONTHS', interval: constructPredefinedInterval({ value: 'LAST_6_MONTHS' }, today) },
    groupBy: 'NONE',
    segmentBy: 'TIME',
    timestep: 'MONTH',
    chartType: 'MULTILINE',
    tabName: 'net-worth',
    today,
    accountingType: 'CASH',
    breadcrumbs: [],
    filter: mkFilterObject(),
    options: {},
    displayOptions: defaultDisplayOptions,
    loading: false,
    usage: businessEnabled ? openBusinessesIds.concat(['PERSONAL']) : undefined,
  },
  taxes: businessEnabled
    ? {
        name: 'taxes',
        reportType: 'SUMMARY',
        reportSubType: 'TAX_SUMMARY',
        chartType: 'NONE',
        groupBy: 'BUSINESS',
        segmentBy: 'TIME',
        dateRange: { id: 'YEAR_TO_DATE', interval: constructPredefinedInterval({ value: 'YEAR_TO_DATE' }, today) },
        timestep: 'YEAR',
        tabName: 'taxes',
        today,
        accountingType: 'CASH',
        breadcrumbs: [],
        filter: mkFilterObject(),
        options: {
          viewToShow: 'GRID',
          hideReportHeader: true,
        },
        displayOptions: defaultDisplayOptions,
        loading: false,
        usage: defaultBusinessId ? [defaultBusinessId] : undefined,
      }
    : {
        name: 'taxes',
        reportType: 'TRANSACTION',
        reportSubType: 'TAX_SUMMARY',
        chartType: 'NONE',
        groupBy: 'USAGE',
        segmentBy: 'NONE',
        dateRange: { id: 'YEAR_TO_DATE', interval: constructPredefinedInterval({ value: 'YEAR_TO_DATE' }, today) },
        tabName: 'taxes',
        today,
        accountingType: 'CASH',
        breadcrumbs: [],
        filter: mkFilterObject(),
        options: {
          viewToShow: 'GRID',
          hideReportHeader: true,
        },
        displayOptions: defaultDisplayOptions,
        usage: ['PERSONAL'],
        loading: false,
      },
  'balance-sheet': {
    name: 'balance-sheet',
    reportType: 'SUMMARY',
    reportSubType: 'BALANCE_SHEET',
    dateRange: { id: 'YEAR_TO_DATE', interval: constructPredefinedInterval({ value: 'YEAR_TO_DATE' }, today) },
    groupBy: 'BUSINESS',
    segmentBy: 'TIME',
    timestep: 'YEAR',
    tabName: 'balance-sheet',
    today,
    accountingType: 'CASH',
    breadcrumbs: [],
    filter: mkFilterObject(),
    chartType: 'NONE',
    options: {
      viewToShow: 'GRAPH_AND_GRID',
      showInterval: false,
    },
    displayOptions: defaultDisplayOptions,
    loading: false,
    usage: defaultBusinessId ? [defaultBusinessId] : undefined,
  },
});
const memoizedbuildDefaultsByTabName = memoizeOne(buildDefaultsByTabName);
export const getDefaultForTabName = (
  tabName: ReportNameType,
  businessEnabled: boolean,
  defaultBusinessId?: string,
  openBusinessesIds?: string[],
): ReportsState =>
  memoizedbuildDefaultsByTabName(
    getStartOfToday(DateTime.local()),
    businessEnabled,
    defaultBusinessId,
    openBusinessesIds,
  )[tabName];

//-------------------------------------------------------------------------------------------------
// GroupBy Options
//-------------------------------------------------------------------------------------------------

export function getDefaultGroupByFromTabName(tabName: ReportNameType) {
  const mapping = {
    spending: 'CATEGORY',
    income: 'NONE',
    'net-income': 'NONE',
    'profit-loss': 'NONE',
    savings: 'NONE',
    'net-worth': 'NONE',
    taxes: 'BUSINESS',
    'balance-sheet': 'BUSINESS',
    custom: 'CATEGORY',
  };
  // @ts-expect-error FIXME TS7053
  return mapping[tabName] || 'CATEGORY';
}

// These group-by options are deprecated

const oldGroupByAccount = { label: 'By account', value: 'ACCOUNT', id: 'breakdown_by_account' };
const oldGroupByCategory = { label: 'By category', value: 'CATEGORY', id: 'breakdown_by_category' };
const oldGroupByPayee = { label: 'By payee', value: 'PAYEE', id: 'breakdown_by_payee' };
const oldGroupByTag = { label: 'By tag', value: 'TAG', id: 'breakdown_by_tag' };
const oldGroupByNone = { label: 'By none', value: 'NONE', id: 'breakdown_by_none' };

const categoryOptions = [oldGroupByCategory, oldGroupByPayee, oldGroupByTag, oldGroupByNone];

const savingsOptions = [oldGroupByAccount, oldGroupByNone];

const customGroupByOptions = [oldGroupByAccount, oldGroupByCategory, oldGroupByPayee, oldGroupByTag, oldGroupByNone];

export function getFilterOptionsFromTabName(tabName: ReportNameType | 'overview') {
  switch (tabName) {
    case 'overview':
    case 'total-spending':
    case 'income':
      return categoryOptions;
    case 'savings':
      return savingsOptions;
    case 'custom':
      return customGroupByOptions;
    case 'net-worth':
    case 'net-income':
    case 'taxes':
    case 'balance-sheet':
    default:
      return null;
  }
}

//-------------------------------------------------------------------------------------------------
// SegmentBy Options
//-------------------------------------------------------------------------------------------------

const tabNameToIsOverTime = {
  spending: false,
  income: true,
  'net-income': true,
  'profit-loss': true,
  savings: true,
  'net-worth': true,
  'refund-tracker': null,
  taxes: false,
  'balance-sheet': false,
  custom: true,
};
export function getDefaultIsOverTimeFromTabName(tabName: ReportNameType) {
  // @ts-expect-error FIXME TS7053
  return tabNameToIsOverTime[tabName];
}

//-------------------------------------------------------------------------------------------------
// Timestep Options
//-------------------------------------------------------------------------------------------------

export const OverTimeOptions = [
  { label: 'Total', value: 'total', id: 'total_total' },
  { label: 'Over time', value: 'over-time', id: 'total_over_time' },
];

//-------------------------------------------------------------------------------------------------
// View to Show Helpers
//-------------------------------------------------------------------------------------------------

export const isGraphSupportedForReportSubType = (reportSubType: ReportSubTypeWebReports) =>
  reportSubType !== 'TAX_SUMMARY' && reportSubType !== 'BALANCE_SHEET';

export const isGridSupportedForReportSubType = (_reportSubType: ReportSubTypeWebReports) => true;

export const areMultipleViewsSupportedForReportSubType = (reportSubType: ReportSubTypeWebReports) =>
  isGraphSupportedForReportSubType(reportSubType) &&
  isGridSupportedForReportSubType(reportSubType) &&
  reportSubType !== 'NET_INCOME';

//-------------------------------------------------------------------------------------------------
// Other...
//-------------------------------------------------------------------------------------------------

// searches through the groups object to find a group with a specific category Id
// also builds the breadcrumbs to the group
// TODO: clean this guy up
export function findGroupByCategoryId(
  catId: string,
  // @ts-expect-error FIXME TS7006
  group,
  crumbs: { name: string; categoryId: string; total: string }[] = [],
) {
  if (!group) return [null, crumbs];
  if (catId === 'EverythingElse' && group.groupKey === 'Everything else') {
    return [group, crumbs.concat([{ name: group.groupKey, categoryId: group.categoryId, total: group.total }])];
  }

  if (group.categoryId === catId) return [group, crumbs];

  let groupFound = null;
  let crumbsFound = crumbs;
  // @ts-expect-error FIXME TS7006
  group.subgroups?.some((subG) => {
    [groupFound, crumbsFound] = findGroupByCategoryId(
      catId,
      subG,
      crumbs.concat([{ name: subG.groupKey, categoryId: subG.categoryId, total: subG.total }]),
    );
    return groupFound; // will cause .some() to short circuit when the group is found recurseively
  });

  return [groupFound, crumbsFound];
}

// Create a stable hashed hex value from the given object
const createHashFromObj = (obj: object) => createHash('sha256').update(stringify(obj)).digest('hex');
const qcsReports = ['profit-loss', 'taxes', 'balance-sheet'];

const arraysEqualUnordered = (a: any[], b: any[]) => a.length === b.length && isEqual([...a].sort(), [...b].sort());

// TODO: this function will change after 4.66.0 to better handle container selections (i.e. "Select All"/"Business"/"Personal")
export const isEqualFilter = (f1: FilterObjectProps, f2: FilterObjectProps): boolean => {
  // Clone and normalize "categories" before comparison — avoid mutation
  // we are going to change how we handle these container categories in an upcoming release.  For now, strip them out.
  const normalizeCategories = (f: FilterObjectProps) =>
    f.categories?.filter((item) => item !== 'USAGE-BUSINESS' && item !== 'USAGE-PERSONAL') ?? [];

  const norm1 = { ...f1, categories: normalizeCategories(f1) };
  const norm2 = { ...f2, categories: normalizeCategories(f2) };

  const allKeys = new Set([...Object.keys(norm1), ...Object.keys(norm2)]);

  for (const key of allKeys) {
    const val1 = norm1[key as keyof FilterObjectProps] || [];
    const val2 = norm2[key as keyof FilterObjectProps] || [];

    // Normalize Immutable.Set to Array for comparison
    const bothAreArrays = Array.isArray(val1) && Array.isArray(val2);
    if (bothAreArrays) {
      if (!arraysEqualUnordered(val1, val2)) return false;
    } else {
      if (!isEqual(val1, val2)) return false;
    }
  }

  return true;
};

export const isEqualState = (state1: ReportsState, state2?: ReportsState): boolean => {
  if (!state2) return false;

  // Destructure and omit non-essential top-level props
  // note: options can't be changed and are web-only so no point in round tripping/coparing them
  const {
    id: _id1,
    clientId: _cId1,
    options: _o1,
    breadcrumbs: _bc1,
    today: _t1,
    defaultBusiness: db1,
    filterId: fid1,
    ...rest1
  } = state1;
  const {
    id: _id2,
    clientId: _cId2,
    options: _o2,
    breadcrumbs: _bc2,
    today: _t2,
    defaultBusiness: db2,
    filterId: fid2,
    ...rest2
  } = state2;

  // There is some strange issue with trying to unset a property which we need to do for timestep between different tax reports.
  // timestep can not be modified in tax reports (not sure why we even need to send it to QCS), so it's ok to skip the comparison.
  if (rest1.reportSubType === 'TAX_SUMMARY') {
    delete rest1.timestep;
  }
  if (rest2.reportSubType === 'TAX_SUMMARY') {
    delete rest2.timestep;
  }

  // Extract `businesses` from filters (skip comparing them here) - these are compared via usage (u1/u2)
  // Extract filters and usage from the rest
  const { filter: filter1, usage: u1, dateRange: dateRange1, ...s1 } = rest1;
  const { filter: filter2, usage: u2, dateRange: dateRange2, ...s2 } = rest2;
  // TODO - advances and flags will need to be handled in another release
  const {
    businesses: _b1,
    id: _fid1,
    numFilterItems: _count1,
    advanced: _a1,
    flags: _flags1,
    clientsAndProjects: _cp1,
    ...f1
  } = filter1?.toJS?.() ?? {};

  const {
    businesses: _b2,
    id: _fid2,
    numFilterItems: _count2,
    advanced: _a2,
    flags: _flags2,
    clientsAndProjects: _cp2,
    ...f2
  } = filter2?.toJS?.() ?? {};

  // Compare usage arrays if QCS report (order is insignificant)
  const isQCSReport = qcsReports.includes(state2.tabName);
  if (isQCSReport && !isEqual(sortBy(u1), sortBy(u2))) return false;

  if (dateRange1.id !== dateRange2.id) return false;
  if (dateRange1.id === 'CUSTOM' && dateRange2.id === 'CUSTOM') {
    if (dateRange1.interval.start?.startOf('day').toMillis() !== dateRange2.interval.start?.startOf('day').toMillis())
      return false;
    if (dateRange1.interval.end?.startOf('day').toMillis() !== dateRange2.interval.end?.startOf('day').toMillis())
      return false;
  }

  // Compare root objects minus `filters` which need special handling because they are arrays
  // and have some special values we need to handle
  if (createHashFromObj(s1) !== createHashFromObj(s2)) return false;

  // Compare filters
  if (!isEqualFilter(f1 as FilterObjectProps, f2 as FilterObjectProps)) return false;

  return true;
};
